/* eslint-disable no-nested-ternary */
import { useQuery } from "@apollo/client";
import { PopoverPortal } from "@radix-ui/react-popover";
import ScreenerFilterableIndicatorsQuery from "components/screener/v2/query/ScreenerFilterableIndicatorsQuery";
import type {
  IScreenerFilterableIndicatorsQuery,
  IScreenerFilterableIndicatorsQueryVariables,
} from "components/screener/v2/query/__generated__/ScreenerFilterableIndicatorsQuery.generated";
import { useSpecificSetting } from "components/settings/hooks/useSpecificSetting";
import { Badge } from "components/ui/Badge";
import { Input } from "components/ui/Input";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "components/ui/Popover";
import { ScrollArea } from "components/ui/ScrollArea";
import { Separator } from "components/ui/Separator";
import classNames from "components/ui/classNames";
import { getFlagValue } from "lib/flagging/FeatureFlags";
import useSelfie from "lib/hooks/useSelfie";
import trimMiddle from "lib/utils/trimMiddle";
import capitalize from "lodash/capitalize";
import flatten from "lodash/flatten";
import groupBy from "lodash/groupBy";
import range from "lodash/range";
import uniqBy from "lodash/uniqBy";
import values from "lodash/values";
import {
  ChevronLeftIcon,
  CircleCheckIcon,
  LightbulbIcon,
  ListCheckIcon,
  LockIcon,
  SearchIcon,
  StarIcon,
} from "lucide-react";
import { matchSorter } from "match-sorter";
import type { MouseEvent, ReactElement } from "react";
import { startTransition, useEffect, useId, useReducer, useRef, useState } from "react";
import Highlighter from "react-highlight-words";
import { useHotkeys } from "react-hotkeys-hook";
import { FormattedMessage, useIntl } from "react-intl";
import removeAccents from "remove-accents";
import type { IFinancialsStatementType, IIndicatorDataSource, IPlatformVertical } from "types/graphqlTypes";
import { IIndicatorCategory } from "types/graphqlTypes";
import { useMediaQuery } from "usehooks-ts";

export type FilterableIndicator = IScreenerFilterableIndicatorsQuery["screenerFilterableIndicators"][number];
interface ScreenerFilterableIndicatorsProps {
  language: "pt-BR" | "en-US";
  vertical: IPlatformVertical;
  primaryDataSource: IIndicatorDataSource;
  statementType: IFinancialsStatementType;
  pendingCount?: number;
  removedCount?: number;
  selectedIndicators?: FilterableIndicator[];
  onSelect: (indicator: FilterableIndicator, event?: MouseEvent) => void;
  onRemove: (indicator: FilterableIndicator, event?: MouseEvent) => void;
  onRemoveAll: () => void;
}
export function ScreenerFilterableIndicators({
  language,
  vertical,
  primaryDataSource,
  statementType,
  pendingCount = 0,
  removedCount = 0,
  selectedIndicators,
  onSelect,
  onRemove,
  onRemoveAll,
}: ScreenerFilterableIndicatorsProps) {
  const intl = useIntl();
  const selfie = useSelfie();
  const { data, loading } = useQuery<IScreenerFilterableIndicatorsQuery, IScreenerFilterableIndicatorsQueryVariables>(
    ScreenerFilterableIndicatorsQuery,
    {
      skip: !getFlagValue("features/isNewScreenerEnabled"),
      variables: {
        input: {
          language,
          vertical,
          primaryDataSource,
          statementType,
        },
      },
    },
  );
  const searchQueryRef = useRef<HTMLInputElement | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const { value: bookmarkedIndicators, set: setBookmarkedIndicators } = useSpecificSetting(
    "screener.bookmarkedIndicators",
    [] as string[],
  );
  const screenerFilterableIndicators = data?.screenerFilterableIndicators || [];
  const availableBookmarkedIndicators = bookmarkedIndicators.filter((candidate) => {
    return screenerFilterableIndicators.some((indicator) => indicator.objectId === candidate);
  });
  const [filters, setFilters] = useState<FilterTag[]>([]);
  useEffect(() => {
    if (!selectedIndicators?.length) {
      setFilters(filters.filter((candidate) => candidate !== "SELECTED"));
    }
  }, [selectedIndicators?.length]);
  useHotkeys(
    "ctrl+f, meta+f, f3",
    (event) => {
      event.preventDefault();

      if (!isOpen) {
        setIsOpen(true);
        return;
      }

      searchQueryRef.current?.focus();
    },
    {
      // enabled: isOpen,
      preventDefault: isOpen,
      ignoreModifiers: false,
    },
  );

  const selectedCount = selectedIndicators?.length || 0;
  const indicators = matchSorter(
    screenerFilterableIndicators
      .map((candidate) => {
        return {
          ...candidate,
          searchableName: candidate.name.replace(/[^a-zA-Z0-9]/g, ""),
        };
      })
      .filter((candidate) => {
        if (filters.length === 0) {
          // NOTE: Remove % Var indicators from the search just so we don't "dump" a bunch of indicators that the user may
          // not want to see in the 1st place. Those WILL ONLY SHOW UP if the user has selected Var % as a filter
          if (candidate.__typename === "ScreenerPercentValueChangeIndicator") {
            return false;
          }
          return true;
        }
        if (filters.length === 1 && filters.includes("BOOKMARKED")) {
          return availableBookmarkedIndicators?.some((objectId) => objectId === candidate.objectId);
        }
        if (filters.length === 1 && filters.includes("SELECTED")) {
          if (selectedCount === 0) {
            return true;
          }
          return selectedIndicators?.some((selected) => selected.objectId === candidate.objectId);
        }
        if (candidate.__typename === "ScreenerStaticIndicator") {
          return filters.includes("REFERENCE");
        }
        if (candidate.__typename === "ScreenerStandardizedIndicator") {
          if (filters.length === 1 && !filters.includes("%")) {
            if (candidate.category === IIndicatorCategory.BalanceSheet) {
              return filters.includes("BP");
            }
            if (candidate.category === IIndicatorCategory.IncomeStatement) {
              return filters.includes("DRE");
            }
            if (candidate.category === IIndicatorCategory.CashFlow) {
              return filters.includes("DFC");
            }
            if (
              candidate.category === IIndicatorCategory.Ratios ||
              candidate.category === IIndicatorCategory.Valuation
            ) {
              return filters.includes("VALUATION");
            }
            return filters.includes("OTHER");
          }
          return !filters.includes("%");
        }
        if (candidate.__typename === "ScreenerPercentValueChangeIndicator") {
          if (!filters.includes("%")) {
            return false;
          }

          if (filters.length === 2) {
            if (filters.includes("%") && filters.includes("DRE")) {
              return candidate.category === IIndicatorCategory.IncomeStatement;
            }
            if (filters.includes("%") && filters.includes("BP")) {
              return candidate.category === IIndicatorCategory.BalanceSheet;
            }
            if (filters.includes("%") && filters.includes("DFC")) {
              return candidate.category === IIndicatorCategory.CashFlow;
            }
            if (filters.includes("%") && filters.includes("VALUATION")) {
              return (
                candidate.category === IIndicatorCategory.Ratios || candidate.category === IIndicatorCategory.Valuation
              );
            }
          }

          return true;
        }
        return false;
      }),
    searchQuery,
    {
      keys: ["name", "abbreviation", "searchableName"],
      keepDiacritics: false,
      threshold: matchSorter.rankings.MATCHES,
      baseSort: (a, b) => {
        if (filters.length === 1 && filters.includes("SELECTED")) {
          const aIndex = selectedIndicators?.findIndex(({ objectId }) => objectId === a.item.objectId);
          const bIndex = selectedIndicators?.findIndex(({ objectId }) => objectId === b.item.objectId);
          return (aIndex || 0) - (bIndex || 0);
        }
        return a.item.name.localeCompare(b.item.name, undefined, {
          sensitivity: "base",
          numeric: true,
        });
      },
    },
  ).sort((a, b) => {
    return Number(b.viewerCanSee) - Number(a.viewerCanSee);
  });

  // useEffect(() => {
  //   // The trick is here: `!isOpen`. Only modify state while stuff's closed. We don't want to mess with the user's
  //   // filters
  //   if (
  //     !isOpen &&
  //     availableBookmarkedIndicators.length > 0 &&
  //     filters.length === 0 &&
  //     selectedIndicators?.length === 0
  //   ) {
  //     setFilters(["BOOKMARKED"]);
  //   }
  //   if (
  //     !isOpen &&
  //     filters.length === 0 &&
  //     selectedIndicators &&
  //     selectedIndicators?.length > 0 &&
  //     indicators.length > 0
  //   ) {
  //     setFilters(["SELECTED"]);
  //   }
  //   if (
  //     !isOpen &&
  //     filters.length === 1 &&
  //     filters.includes("SELECTED") &&
  //     selectedIndicators &&
  //     selectedIndicators?.length > 0 &&
  //     indicators.length === 0
  //   ) {
  //     setFilters(["BOOKMARKED"]);
  //   }
  // }, [
  //   loading,
  //   isOpen,
  //   availableBookmarkedIndicators.length,
  //   selectedIndicators?.length,
  //   filters.length,
  //   indicators.length,
  // ]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setFilters([]);
    }
  }, [isOpen]);

  useEffect(() => {
    const isPersonalFilters = filters.includes("BOOKMARKED") || filters.includes("SELECTED");
    if (searchQuery.trim() !== "" && indicators.length === 0 && isPersonalFilters) {
      setFilters([]);
    }
  }, [searchQuery]);

  const namespaceID = useId();
  const duplicateIndicatorLabels = flatten(
    values(
      groupBy(indicators.slice(0, 90), (indicator) => {
        return indicator.name;
      }),
    ).filter((group) => group.length >= 2),
  ).map((indicator) => indicator.objectId);
  const isDesktopWidth = useMediaQuery("(min-width: 1024px)");
  const pointRef = useRef({ x: 0, y: 0 });
  const virtualPopoverRef = useRef({
    getBoundingClientRect: () => DOMRect.fromRect({ width: 0, height: 0, ...pointRef.current }),
  });
  const isMobile = useMediaQuery("(max-width: 639px)");

  // useHotkeys(
  //   ["ctrl+1", "ctrl+2", "ctrl+3", "meta+1", "meta+2", "meta+3"],
  //   (_event, { keys }) => {
  //     if (Array.isArray(keys) && keys.length === 1 && typeof keys[0] === "string" && /^([\d+])$/.test(keys[0])) {
  //       const index = parseInt(keys[0], 10);
  //       if (indicators.length > 0 && index <= indicators.length) {
  //         const indicator = indicators[index - 1];
  //         if (indicator) {
  //           onSelect(indicator);
  //           setIsOpen(false);
  //         }
  //       }
  //     }
  //   },
  //   {
  //     preventDefault: true,
  //     enabled: isOpen && searchQuery.trim() !== "" && indicators.length > 0,
  //   },
  // );

  return (
    <Popover modal={true} open={isOpen} onOpenChange={(isOpen) => setIsOpen(isOpen)}>
      {isOpen && (
        <PopoverPortal>
          <div className="absolute bottom-0 left-0 right-0 top-0 z-10 bg-black/5 backdrop-blur-sm" />
        </PopoverPortal>
      )}
      {isOpen && isMobile ? (
        <PopoverAnchor virtualRef={virtualPopoverRef} />
      ) : (
        <PopoverTrigger asChild={true}>
          <button
            type="button"
            role="checkbox"
            aria-checked={isOpen ? "true" : "false"}
            className={classNames(
              "flex h-10 items-center gap-x-2 pl-3 pr-3 lg:pr-4",
              "relative isolate rounded-full border border-slate-300 bg-white text-slate-600",
              "dark:border-slate-700 dark:bg-slate-800",
              "hover:bg-slate-100",
              "aria-checked:cursor-pointer aria-checked:border-viridian-500/30 aria-checked:bg-viridian-100 aria-checked:text-viridian-600",
              "dark:aria-checked:border-emerald-400/10 dark:aria-checked:bg-emerald-400/10 dark:aria-checked:text-emerald-300",
              "group",
              "hover:border-viridian-500/30 hover:bg-viridian-50 hover:text-viridian-600",
              "dark:border-viridian-500/30 dark:bg-viridian-600/30 dark:text-lime-300 dark:hover:opacity-80",
              "focus:outline-none",
              { "z-50": isOpen },
            )}
          >
            <SearchIcon
              className={classNames(
                "fill-slate-50 stroke-slate-600 dark:fill-slate-700 dark:stroke-slate-300",
                "group-hover:fill-viridian-50 group-hover:stroke-viridian-600",
                "dark:stroke-lime-400 dark:group-hover:fill-transparent dark:group-hover:stroke-lime-400",
                "group-aria-checked:fill-viridian-100 group-aria-checked:stroke-viridian-500",
                "dark:group-aria-checked:fill-emerald-400/10 dark:group-aria-checked:stroke-emerald-400",
                "size-4",
              )}
              strokeWidth={2.5}
              absoluteStrokeWidth={true}
            />
            <strong className="hidden text-sm font-bold lg:inline-flex">
              <FormattedMessage defaultMessage="Indicadores" id="GUCl4s" />
            </strong>
          </button>
        </PopoverTrigger>
      )}

      <PopoverContent
        className={classNames(
          "relative flex flex-col gap-y-2 rounded-2xl border border-zinc-300 py-2 shadow-md",
          "dark:border-slate-700",
          {
            "w-80 max-w-xs": !isMobile,
            "m-4 w-[calc(100vw-theme('spacing.8'))]": isMobile,
          },
        )}
        side={isMobile ? "top" : "bottom"}
        align={isMobile ? "start" : "start"}
        sideOffset={isMobile ? 0 : 4}
      >
        <div className="flex items-center gap-x-2 px-1">
          <button
            type="button"
            className="rounded-full bg-slate-100 p-1 hover:bg-slate-200 dark:bg-slate-950"
            onClick={() => setIsOpen(false)}
          >
            <ChevronLeftIcon
              absoluteStrokeWidth={true}
              strokeWidth={2}
              className="size-6 stroke-slate-600 dark:stroke-slate-300"
            />
          </button>
          <Input
            ref={searchQueryRef}
            autoFocus={isDesktopWidth}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "f" && isOpen) {
                setIsOpen(false);
                return;
              }
              if (e.key === "Enter" && searchQuery.trim() !== "" && indicators.length > 0) {
                const isSelected = selectedIndicators?.some((candidate) => {
                  return candidate.objectId === indicators[0].objectId;
                });
                if (!isSelected) {
                  onSelect(indicators[0]);
                }
                setIsOpen(false);
              }
            }}
            placeholder={intl.formatMessage({ defaultMessage: "Pesquise...", id: "lYByRF" })}
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            autoSave="off"
            maxLength={32}
            className={classNames(
              "h-10 rounded-xl border-gray-300 text-base",
              "placeholder:font-medium",
              "focus:border-transparent focus:bg-blue-50 focus:ring-[1.5px] focus:ring-inset focus:ring-blue-400 focus:ring-offset-transparent",
              "md:text-sm",
              "font-medium",
              "dark:focus:bg-blue-400/10 dark:focus-visible:outline-transparent",
            )}
          />
        </div>
        <Separator />
        <Filters
          filters={filters}
          onChange={(nextFilters) => {
            if (
              nextFilters.length === 1 &&
              nextFilters.includes("BOOKMARKED") &&
              !filters.includes("BOOKMARKED") &&
              searchQuery.trim() !== ""
            ) {
              startTransition(() => {
                setFilters(nextFilters);
                setSearchQuery("");
              });
              return;
            }
            setFilters(nextFilters);
          }}
          selectedCount={selectedCount}
          bookmarkedCount={availableBookmarkedIndicators?.length || 0}
        />
        <Separator />
        <ScrollArea tabIndex={0}>
          {indicators.length === 0 && !loading && (
            <p
              className={classNames(
                "mx-1 flex flex-col items-center justify-center gap-x-0.5 gap-y-2 rounded-md bg-slate-50 p-4 text-base font-medium tracking-tight text-slate-600",
                "dark:bg-slate-950 dark:text-slate-400",
              )}
            >
              <SearchIcon className="inline-flex size-6 fill-zinc-100 stroke-zinc-500 dark:fill-transparent dark:stroke-zinc-300" />
              <FormattedMessage defaultMessage="Nada encontrado..." id="kFeIbv" />
            </p>
          )}
          {loading && (
            <div className="flex flex-col gap-y-2 px-2 py-1" aria-hidden={true}>
              {range(0, 7).map((item) => {
                return (
                  <div
                    key={item}
                    className={classNames(
                      "h-4 w-full animate-pulse rounded-md bg-slate-200",
                      "dark:bg-slate-700 dark:ring-slate-700 dark:ring-offset-slate-700",
                      "flex items-center justify-center",
                    )}
                  />
                );
              })}
            </div>
          )}
          <ul className="flex max-h-96 select-none flex-col gap-y-1.5 px-1 empty:hidden lg:gap-y-1">
            {indicators.slice(0, 90).map((node) => {
              const isSelected = selectedIndicators?.some((candidate) => candidate.objectId === node.objectId);
              const isBookmarkedIndicator = Array.isArray(availableBookmarkedIndicators)
                ? availableBookmarkedIndicators.includes(node.objectId)
                : false;
              const { viewerCanSee } = node;
              const viewerCannotSee = !viewerCanSee;
              const isDuplicatedInList = duplicateIndicatorLabels.includes(node.objectId);
              let duplicationSuffix =
                isDuplicatedInList &&
                (node.__typename === "ScreenerPercentValueChangeIndicator" ||
                  node.__typename === "ScreenerStandardizedIndicator")
                  ? capitalize(node.dataSource)
                  : "";
              if (isDuplicatedInList && node.__typename === "ScreenerStaticIndicator") {
                duplicationSuffix = intl.formatMessage({ defaultMessage: "Estático", id: "0Y+lCA" });
              }
              let { name, abbreviation } = node;
              // Se a busca não der match no nome do indicador, mas der na abreviação, mostra a abreviação pro usuário
              if (
                searchQuery &&
                !name.toLowerCase().includes(searchQuery) &&
                abbreviation.toLowerCase().includes(searchQuery)
              ) {
                name = abbreviation;
              }
              const wordTokens = searchQuery
                .trim()
                .split(/\s+/)
                .filter((candidate) => candidate.length >= 3);
              return (
                <li
                  key={node.objectId}
                  className={classNames("group flex select-none items-center gap-x-1", {
                    "pr-3": indicators.length > 10,
                  })}
                >
                  <button
                    type="button"
                    disabled={viewerCannotSee}
                    className={classNames(
                      "group min-h-9 hover:font-bold lg:min-h-6",
                      "bg-transparent dark:bg-slate-500/10",
                      "w-full rounded-xl px-2 py-1 text-left text-sm font-medium",
                      "flex items-center justify-between",

                      "text-gray-600 dark:font-bold dark:text-gray-300",

                      "hover:bg-viridian-50 hover:text-viridian-600",
                      "dark:hover:bg-lime-400/10 dark:hover:text-lime-200",
                      "disabled:bg-rose-50 disabled:dark:bg-rose-400/10",
                      {
                        "disabled:bg-slate-50 disabled:dark:bg-slate-400/10": !isSelected,
                      },
                      {
                        "bg-viridian-50 font-bold text-viridian-600": isSelected,
                        "dark:bg-lime-400/10 dark:text-lime-200": isSelected,
                        "hover:bg-slate-50 dark:hover:bg-slate-400/10": viewerCannotSee,
                      },
                    )}
                    onContextMenu={(e) => {
                      if (e.ctrlKey) {
                        e.preventDefault();
                        if (isSelected) {
                          onRemove(node, e);
                        } else {
                          onSelect(node, e);
                        }
                        setIsOpen(e.ctrlKey || e.metaKey);
                      }
                    }}
                    onClick={(e) => {
                      if (isSelected) {
                        onRemove(node, e);
                      } else {
                        onSelect(node, e);
                      }
                      setIsOpen(e.ctrlKey || e.metaKey);
                    }}
                  >
                    <Highlighter
                      popovertarget={namespaceID + node.objectId}
                      className={classNames(
                        "line-clamp-2 w-60 text-sm",
                        "group-disabled:text-slate-600 group-disabled:dark:text-slate-300",
                        { "group-disabled:text-rose-600 group-disabled:dark:text-rose-300": isSelected },
                      )}
                      highlightClassName={classNames(
                        "rounded-md bg-navy-800/5 px-1.5 py-0.5 font-bold text-navy-600",
                        "dark:bg-yellow-400/20 dark:text-yellow-100",
                      )}
                      searchWords={[searchQuery.trim(), ...wordTokens]}
                      autoEscape={false}
                      sanitize={removeAccents}
                      textToHighlight={trimMiddle(
                        duplicationSuffix ? `${name} / ${duplicationSuffix}` : name,
                        60,
                        "...",
                      )}
                    />

                    {isSelected && (
                      <CircleCheckIcon
                        aria-hidden={true}
                        className="size-4 fill-viridian-100 stroke-viridian-500 dark:fill-lime-400/10 dark:stroke-lime-400"
                        absoluteStrokeWidth={true}
                        strokeWidth={2}
                      />
                    )}

                    {!isSelected && !viewerCannotSee && (
                      <CircleCheckIcon
                        aria-hidden={true}
                        className={classNames(
                          "invisible fill-viridian-100 stroke-viridian-500",
                          "group-hover:visible",
                          "size-4",
                          "dark:fill-lime-400/10 dark:stroke-lime-400",
                        )}
                        absoluteStrokeWidth={true}
                        strokeWidth={2}
                      />
                    )}

                    {viewerCannotSee && (
                      <LockIcon
                        aria-hidden={true}
                        className={classNames(
                          "fill-red-100 stroke-red-600",
                          "dark:fill-slate-400/10 dark:stroke-slate-300",
                          { "dark:fill-rose-400/10 dark:stroke-rose-300": isSelected },
                          "size-4",
                        )}
                        absoluteStrokeWidth={true}
                        strokeWidth={2}
                      />
                    )}
                  </button>
                  {selfie?.isPremium && (
                    <button
                      role="checkbox"
                      disabled={viewerCannotSee}
                      aria-checked={isBookmarkedIndicator}
                      type="button"
                      className={classNames("group group-hover:opacity-100 aria-checked:visible", {
                        "pointer-events-none opacity-50": viewerCannotSee,
                        "opacity-25": !isBookmarkedIndicator,
                      })}
                      title={intl.formatMessage({ defaultMessage: "Favoritar", id: "96qTSK" })}
                      onClick={() => {
                        if (Array.isArray(bookmarkedIndicators) && !isBookmarkedIndicator) {
                          setBookmarkedIndicators([...bookmarkedIndicators, node.objectId]);
                        } else {
                          setBookmarkedIndicators(
                            bookmarkedIndicators.filter((candidate) => candidate !== node.objectId),
                          );
                        }
                      }}
                    >
                      <StarIcon
                        strokeWidth={2}
                        absoluteStrokeWidth={true}
                        className={classNames(
                          "fill-slate-100 stroke-slate-600 transition-transform dark:stroke-transparent",
                          "hover:scale-150 hover:fill-orange-100 hover:stroke-orange-600 dark:hover:fill-orange-300",
                          "group-aria-checked:fill-orange-100 group-aria-checked:stroke-orange-600",
                          "dark:group-aria-checked:fill-orange-500 dark:group-aria-checked:stroke-orange-500",
                          "dark:stroke-1",
                          "size-[18px]",
                        )}
                      />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </ScrollArea>
        {filters.includes("SELECTED") && indicators.length >= 2 && (
          <div className="px-1">
            <button
              onClick={() => {
                onRemoveAll();
                setIsOpen(false);
              }}
              type="button"
              className={classNames(
                "flex w-full items-center justify-center rounded-xl bg-slate-50 py-1.5 text-sm font-bold text-slate-600",
                "text-rose-700 hover:bg-rose-100",
                "dark:bg-slate-700/50 dark:text-slate-300",
                "dark:text-rose-200 dark:hover:bg-rose-400/30",
              )}
            >
              <FormattedMessage defaultMessage="Remover Todos" id="SM3nHx" />
            </button>
          </div>
        )}
        <Separator className="hidden lg:flex" />
        <div className="hidden items-center justify-between px-1 lg:flex">
          <IsHoldingCtrl>
            {(isHoldingCtrl) => {
              const isHoldingWithPending = (pendingCount > 0 || removedCount > 0) && isHoldingCtrl;
              return (
                <p
                  className={classNames(
                    "flex items-center gap-x-0.5 text-sm font-medium tracking-tight text-slate-600 dark:text-slate-400",
                    { "animate-pulse font-bold": isHoldingWithPending },
                  )}
                >
                  {isHoldingWithPending ? (
                    <>
                      <ListCheckIcon className="inline-flex size-4" />
                      <FormattedMessage
                        defaultMessage="Solte {ctrl} para confirmar"
                        id="fCQhe/"
                        values={{
                          ctrl: (
                            <kbd
                              className={classNames(
                                "rounded-md bg-orange-50 px-1 font-mono45 text-xs font-bold text-orange-600",
                                "dark:bg-orange-400/20 dark:text-orange-300",
                              )}
                            >
                              Ctrl
                            </kbd>
                          ),
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <LightbulbIcon className="inline-flex size-4" />
                      <FormattedMessage
                        defaultMessage="Segure {ctrl} para selecionar vários"
                        id="46orZ7"
                        values={{
                          ctrl: (
                            <kbd className="rounded-md bg-slate-100 px-1 font-mono45 text-xs font-bold dark:bg-slate-700 dark:text-white">
                              Ctrl
                            </kbd>
                          ),
                        }}
                      />
                    </>
                  )}
                </p>
              );
            }}
          </IsHoldingCtrl>
          <Badge variant="neutral">
            <FormattedMessage
              defaultMessage="Total {totalCount}"
              id="k2h1LD"
              values={{
                totalCount: indicators.length > 999 ? "1k+" : indicators.length,
              }}
            />
          </Badge>
        </div>
      </PopoverContent>
    </Popover>
  );
}

type FilterTag =
  | "REFERENCE"
  | "BP"
  | "DRE"
  | "DFC"
  | "%"
  | "VALUATION"
  | "PAYOUT"
  | "OTHER"
  | "SELECTED"
  | "BOOKMARKED";
interface FiltersProps {
  filters: FilterTag[];
  onChange: (filters: FilterTag[]) => void;
  selectedCount?: number;
  bookmarkedCount?: number;
}
function Filters({ filters, onChange, selectedCount = 0, bookmarkedCount = 0 }: FiltersProps) {
  const toggleFilter = (filter: FilterTag) => {
    const nextFilters = filters.includes(filter)
      ? filters.filter((candidate) => candidate !== filter)
      : [...filters, filter].filter((candidate) => candidate !== "SELECTED" && candidate !== "BOOKMARKED");
    onChange(nextFilters);
  };
  const isActive = (filter: FilterTag) => filters.includes(filter);
  return (
    <div className="mx-1 flex flex-wrap items-center gap-x-1 gap-y-1.5">
      <button type="button" onClick={() => onChange([])}>
        <Badge variant={filters.length > 0 ? "default" : "olive"}>
          <FormattedMessage defaultMessage="Todos" id="P8VfZA" />
        </Badge>
      </button>
      <button type="button" disabled={selectedCount === 0} onClick={() => onChange(["SELECTED"])}>
        <Badge variant={isActive("SELECTED") ? "indigo" : selectedCount === 0 ? "disabled" : "default"}>
          <FormattedMessage defaultMessage="Selecionados" id="SkEqwT" />
        </Badge>
      </button>
      {bookmarkedCount > 0 && (
        <button type="button" onClick={() => onChange(["BOOKMARKED"])}>
          <Badge variant={isActive("BOOKMARKED") ? "orange" : "default"}>
            <FormattedMessage defaultMessage="Favoritos" id="g2IZZj" />
          </Badge>
        </button>
      )}
      <button type="button" onClick={() => onChange(["REFERENCE"])}>
        <Badge variant={isActive("REFERENCE") ? "chestnut" : "default"}>
          <FormattedMessage defaultMessage="Referência" id="AWUTmz" />
        </Badge>
      </button>
      <button type="button" onClick={() => onChange(["BP"])}>
        <Badge variant={isActive("BP") ? "red" : "default"}>
          <FormattedMessage defaultMessage="BP" id="/Q0taG" />
        </Badge>
      </button>
      <button type="button" onClick={() => onChange(["DRE"])}>
        <Badge variant={isActive("DRE") ? "green" : "default"}>
          <FormattedMessage defaultMessage="DRE" id="WfdSHb" />
        </Badge>
      </button>
      <button type="button" onClick={() => onChange(["DFC"])}>
        <Badge variant={isActive("DFC") ? "navy" : "default"}>
          <FormattedMessage defaultMessage="DFC" id="+1/1B+" />
        </Badge>
      </button>
      <button type="button" onClick={() => onChange(["VALUATION"])}>
        <Badge variant={isActive("VALUATION") ? "purple" : "default"}>
          <FormattedMessage defaultMessage="Valuation" id="qM+1zP" />
        </Badge>
      </button>
      <button type="button" onClick={() => onChange(["OTHER"])}>
        <Badge variant={isActive("OTHER") ? "cyan" : "default"}>
          <FormattedMessage defaultMessage="Outros" id="N7uKhl" />
        </Badge>
      </button>
      <button type="button" onClick={() => toggleFilter("%")}>
        <Badge variant={isActive("%") ? "viridian" : "default"}>
          <FormattedMessage defaultMessage="Var (%)" id="35IYIl" />
        </Badge>
      </button>
    </div>
  );
}

export function useIsHoldingControl() {
  const [isHoldingCtrl, setIsHoldingCtrl] = useState(false);
  useEffect(() => {
    const abortController = new AbortController();
    window.addEventListener("keyup", (e) => setIsHoldingCtrl(e.ctrlKey || e.metaKey), {
      signal: abortController.signal,
    });
    window.addEventListener("keydown", (e) => setIsHoldingCtrl(e.ctrlKey || e.metaKey), {
      signal: abortController.signal,
    });
    return () => {
      return abortController.abort();
    };
  }, []);
  return isHoldingCtrl;
}

interface IsHoldingCtrlCallbackProps {
  onChange: (isHoldingCtrl: boolean) => void;
}

export function IsHoldingCtrlCallback({ onChange }: IsHoldingCtrlCallbackProps) {
  const isHoldingCtrl = useIsHoldingControl();
  useEffect(() => {
    onChange(isHoldingCtrl);
  }, [isHoldingCtrl]);
  return null;
}

interface IsHoldingCtrlProps {
  children: (isHoldingCtrl: boolean) => ReactElement;
}
export function IsHoldingCtrl({ children }: IsHoldingCtrlProps) {
  const isHoldingCtrl = useIsHoldingControl();
  return children(isHoldingCtrl);
}

type SelectDebouncerState = {
  pendingCount: number;
  removedCount: number;
  selectedIndicators?: FilterableIndicator[];
  onSelectIndicator: (indicator: FilterableIndicator, event?: MouseEvent) => void;
  onRemoveIndicator: (indicator: FilterableIndicator, event?: MouseEvent) => void;
  onRemoveAll: () => void;
};
interface SelectDebouncerProps {
  selectedIndicators?: FilterableIndicator[];
  onCommit?: (indicators: FilterableIndicator[]) => FilterableIndicator[] | void;
  children: (state: SelectDebouncerState) => ReactElement;
}
type InternalDebouncerState = {
  pendingIndicators: FilterableIndicator[];
  removedIndicators: FilterableIndicator[];
};
type InternalDebouncerAction =
  | {
      type: "select";
      indicator: FilterableIndicator;
    }
  | {
      type: "remove";
      indicator: FilterableIndicator;
    }
  | {
      type: "commit";
    };
export function SelectDebouncer({ selectedIndicators = [], onCommit, children }: SelectDebouncerProps) {
  const [{ pendingIndicators, removedIndicators }, performAction] = useReducer(
    (state: InternalDebouncerState, action: InternalDebouncerAction) => {
      if (action.type === "select") {
        return {
          pendingIndicators: uniqBy(state.pendingIndicators.concat(action.indicator), "objectId"),
          removedIndicators: state.removedIndicators.filter(({ objectId }) => objectId !== action.indicator.objectId),
        };
      }
      if (action.type === "remove") {
        return {
          pendingIndicators: state.pendingIndicators.filter(({ objectId }) => objectId !== action.indicator.objectId),
          removedIndicators: uniqBy(
            state.removedIndicators.concat(action.indicator).filter(({ objectId }) => {
              return selectedIndicators.some((indicator) => indicator.objectId === objectId);
            }),
            "objectId",
          ),
        };
      }
      if (action.type === "commit") {
        return {
          pendingIndicators: [],
          removedIndicators: [],
        };
      }
      return state;
    },
    {
      pendingIndicators: [],
      removedIndicators: [],
    },
  );
  const isHoldingCtrl = useIsHoldingControl();

  const calculateAndCommit = ({
    pending,
    removed,
    edge = "start",
  }: {
    pending?: FilterableIndicator[];
    removed?: FilterableIndicator[];
    edge?: "start" | "end";
  } = {}) => {
    const resolvedPending = pending || pendingIndicators;
    const resolvedRemoved = removed || removedIndicators;
    if (resolvedPending.length === 0 && resolvedRemoved.length === 0) {
      return;
    }
    const indicators =
      edge === "start" ? resolvedPending.concat(selectedIndicators) : selectedIndicators.concat(resolvedPending);
    const resolvedSelectedIndicators = indicators.filter((indicator) => {
      return !resolvedRemoved.some(({ objectId }) => objectId === indicator.objectId);
    });
    startTransition(() => {
      performAction({ type: "commit" });
      onCommit?.(resolvedSelectedIndicators);
    });
  };

  useEffect(() => {
    if (!isHoldingCtrl) {
      calculateAndCommit();
    }
  }, [isHoldingCtrl]);
  // const isDesktopWidth = useMediaQuery("(min-width: 1024px)");
  return children({
    selectedIndicators: selectedIndicators.concat(pendingIndicators).filter((indicator) => {
      return !removedIndicators.some(({ objectId }) => objectId === indicator.objectId);
    }),
    pendingCount: pendingIndicators.length,
    removedCount: removedIndicators.length,
    onRemoveIndicator: (indicator: FilterableIndicator, event?: MouseEvent) => {
      const isCtrlEvent = event?.ctrlKey || event?.metaKey;
      if (isCtrlEvent) {
        performAction({
          type: "remove",
          indicator,
        });
      } else {
        calculateAndCommit({ removed: [indicator] });
      }
    },
    onSelectIndicator: (indicator: FilterableIndicator, event?: MouseEvent) => {
      const isCtrlEvent = event?.ctrlKey || event?.metaKey;
      if (isCtrlEvent) {
        performAction({
          type: "select",
          indicator,
        });
      } else {
        calculateAndCommit({
          pending: [indicator],
          // edge: isDesktopWidth ? "end" : "start",
          edge: "start",
        });
      }
    },
    onRemoveAll: () => {
      startTransition(() => {
        performAction({ type: "commit" });
        onCommit?.([]);
      });
    },
  });
}
